/**
 * reviewHighlight - mark the exact words a finding changes in the document
 * (F-REVIEW, "surgical highlighting").
 *
 * The viewer used to point at a finding by its section number or the first
 * eight words of its clause - so a one-word change in a ten-line clause lit
 * up the clause, and the reader still had to find the word. This:
 *
 *   1. indexes the document's text (whitespace, case, smart quotes, dashes
 *      and NBSP normalised - the same equivalences the engine grounds with),
 *   2. finds the finding's whole clause (`original_text`) in it,
 *   3. inside that clause only, finds each changed hunk by its context words
 *      (`reviewChange`) and wraps just the removed words in
 *      <mark class="finding-change">, or drops a caret where words are added,
 *   4. puts the finding's anchor id on the first mark, so the existing
 *      scroll-to-finding lands on the change itself.
 *
 * Works on the ORIGINAL document, where the old words are. A finding that
 * can't be placed this way is returned as unplaced, and the caller keeps the
 * old clause-level anchor for it. Pure DOM, no layout - testable in jsdom.
 */

import { reviewChange } from './reviewDiff';

export interface ChangeFinding {
  uuid: string;
  original_text: string | null;
  suggested_redline: string | null;
  modified_text?: string | null;
  action_type?: string | null;
}

const NORMAL: Record<string, string> = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '“': '"', '”': '"', '„': '"',
  '–': '-', '—': '-', '−': '-',
  ' ': ' ',
};

/** Normalised text plus, for every normalised character, its DOM position. */
interface TextIndex {
  text: string;
  at: Array<{ node: Text; offset: number }>;
}

const normalize = (s: string) =>
  s.replace(/[‘’‚‛“”„–—− ]/g, (c) => NORMAL[c] || c)
    .replace(/\s+/g, ' ').trim().toLowerCase();

const buildIndex = (root: Node): TextIndex => {
  const doc = root.ownerDocument || (root as Document);
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let text = '';
  const at: TextIndex['at'] = [];
  let lastSpace = true;
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    const raw = n.data;
    for (let i = 0; i < raw.length; i++) {
      let c = NORMAL[raw[i]] || raw[i];
      if (/\s/.test(c)) {
        if (lastSpace) continue;
        c = ' ';
        lastSpace = true;
      } else {
        lastSpace = false;
        c = c.toLowerCase();
      }
      text += c;
      at.push({ node: n, offset: i });
    }
    // Block boundaries read as a space, so "end.</p><p>Start" doesn't glue.
    if (!lastSpace) {
      const parent = n.parentElement;
      const nextBlock = parent && /^(P|LI|TD|TH|H[1-6]|DIV)$/.test(parent.tagName) && !n.nextSibling;
      if (nextBlock) { text += ' '; at.push({ node: n, offset: raw.length }); lastSpace = true; }
    }
  }
  return { text, at };
};

/** Every occurrence of `needle` in `hay` from `from` to `to`. */
const occurrences = (hay: string, needle: string, from: number, to: number): number[] => {
  const out: number[] = [];
  if (!needle) return out;
  let i = hay.indexOf(needle, from);
  while (i !== -1 && i + needle.length <= to) { out.push(i); i = hay.indexOf(needle, i + 1); }
  return out;
};

/** Locate the clause: the whole quote, else its first 120 / 60 characters. */
const findClause = (index: TextIndex, original: string): [number, number] | null => {
  const q = normalize(original);
  if (q.length < 12) return null;
  for (const len of [q.length, 160, 80]) {
    const probe = q.slice(0, Math.min(len, q.length));
    const hits = occurrences(index.text, probe, 0, index.text.length);
    // Ambiguous short probes (a repeated boilerplate sentence) are not a
    // place to point at.
    if (hits.length === 1 || (hits.length > 1 && len === q.length)) {
      return [hits[0], Math.min(index.text.length, hits[0] + q.length)];
    }
  }
  return null;
};

/** Wrap the normalised range [start, end) in <mark>s, one per text node. */
const wrapRange = (index: TextIndex, start: number, end: number, attrs: Record<string, string>): HTMLElement[] => {
  const byNode = new Map<Text, { from: number; to: number }>();
  for (let k = start; k < end; k++) {
    const pos = index.at[k];
    if (!pos || pos.offset >= pos.node.data.length) continue;
    const span = byNode.get(pos.node);
    if (span) span.to = pos.offset + 1;
    else byNode.set(pos.node, { from: pos.offset, to: pos.offset + 1 });
  }
  const marks: HTMLElement[] = [];
  byNode.forEach(({ from, to }, node) => {
    const doc = node.ownerDocument;
    const range = doc.createRange();
    range.setStart(node, from);
    range.setEnd(node, to);
    const mark = doc.createElement('mark');
    Object.entries(attrs).forEach(([k, v]) => mark.setAttribute(k, v));
    try {
      range.surroundContents(mark);
      marks.push(mark);
    } catch {
      /* a range crossing an element boundary - leave this piece unmarked */
    }
  });
  return marks;
};

/** A zero-width marker after normalised position `at`, for pure insertions. */
const caretAfter = (index: TextIndex, at: number, attrs: Record<string, string>): HTMLElement | null => {
  const pos = index.at[Math.max(0, at - 1)];
  if (!pos) return null;
  const node = pos.node;
  const split = Math.min(node.data.length, pos.offset + 1);
  const tail = node.splitText(split);
  const mark = node.ownerDocument.createElement('mark');
  Object.entries(attrs).forEach(([k, v]) => mark.setAttribute(k, v));
  tail.parentNode?.insertBefore(mark, tail);
  return mark;
};

/**
 * Mark every finding's changed words in `root`. Returns the uuids it placed;
 * everything else keeps the caller's clause-level anchor.
 */
export const markChanges = (root: HTMLElement, findings: ChangeFinding[]): Set<string> => {
  const placed = new Set<string>();
  for (const f of findings) {
    if (f.action_type && f.action_type !== 'replace') continue;
    const replacement = f.modified_text || f.suggested_redline;
    const change = reviewChange(f.original_text, replacement);
    if (!change || change.isRewrite || change.hunks.length === 0) continue;

    // Rebuilt per finding: earlier marks split text nodes.
    const index = buildIndex(root);
    const clause = findClause(index, f.original_text || '');
    if (!clause) continue;

    const marks: HTMLElement[] = [];
    let cursor = clause[0];
    for (const h of change.hunks) {
      const before = normalize(h.before);
      const deleted = normalize(h.deleted);
      const after = normalize(h.after);
      const attrs = { class: deleted ? 'finding-change' : 'finding-insert', 'data-finding': f.uuid,
        title: (h.inserted || '').trim() };
      // Find the hunk by its context, inside the clause, after the last one.
      const probe = [before, deleted, after].filter(Boolean).join(' ');
      const hits = occurrences(index.text, probe, cursor, clause[1] + 1);
      if (hits.length === 0) continue;
      const startOfDeleted = hits[0] + (before ? before.length + 1 : 0);
      if (deleted) {
        marks.push(...wrapRange(index, startOfDeleted, startOfDeleted + deleted.length, attrs));
      } else {
        const caret = caretAfter(index, startOfDeleted - (before ? 1 : 0), attrs);
        if (caret) marks.push(caret);
      }
      cursor = startOfDeleted + deleted.length;
      // Later hunks need fresh positions: marking split the text nodes.
      Object.assign(index, buildIndex(root));
    }

    if (marks.length > 0) {
      const doc = root.ownerDocument;
      if (!doc.getElementById(`finding-${f.uuid}`)) marks[0].id = `finding-${f.uuid}`;
      marks.forEach((m) => m.classList.add('finding-anchor'));
      placed.add(f.uuid);
    }
  }
  return placed;
};

/** The same, on an HTML string - for the srcDoc pipeline. */
export const markChangesInHtml = (html: string, findings: ChangeFinding[]): { html: string; placed: Set<string> } => {
  const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root') as HTMLElement;
  const placed = markChanges(root, findings);
  return { html: root.innerHTML, placed };
};

// ---------------------------------------------------------------------------
// PDF (F-REVIEW for PDFs)
//
// A PDF has no DOM to wrap; the viewer's search plugin highlights regular
// expressions in its text layer. So each changed hunk becomes a pattern that
// matches ONLY the removed words, pinned in place by its context with
// look-behind / look-ahead - `(?<=not less than\s+)30(?=\s+days)` - so the
// "30" in the payment clause is not lit up. PDF text breaks lines and pages
// unpredictably, so patterns come in tiers, tried in order: two context words
// each side, then one, then the clause's opening words (the old behaviour).
// ---------------------------------------------------------------------------

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Words → a pattern tolerant of line breaks and smart punctuation. */
const loosePattern = (text: string): string =>
  normalize(text)
    .split(' ')
    .filter(Boolean)
    .map((w) => escapeRe(w)
      .replace(/'/g, "['‘’]")
      .replace(/"/g, '["“”]')
      .replace(/-/g, '[-–—]'))
    .join('\\s+');

const lastN = (s: string, n: number) => normalize(s).split(' ').filter(Boolean).slice(-n).join(' ');
const firstN = (s: string, n: number) => normalize(s).split(' ').filter(Boolean).slice(0, n).join(' ');

export const pdfHighlightPatterns = (f: ChangeFinding): RegExp[][] => {
  const tiers: RegExp[][] = [];
  const replacement = f.modified_text || f.suggested_redline;
  const change = (!f.action_type || f.action_type === 'replace') ? reviewChange(f.original_text, replacement) : null;

  if (change && !change.isRewrite) {
    for (const ctx of [2, 1]) {
      const tier: RegExp[] = [];
      for (const h of change.hunks) {
        const deleted = loosePattern(h.deleted);
        if (!deleted) continue;
        const before = h.before ? loosePattern(lastN(h.before, ctx)) : '';
        const after = h.after ? loosePattern(firstN(h.after, ctx)) : '';
        try {
          tier.push(new RegExp(`${before ? `(?<=${before}\\s+)` : ''}${deleted}${after ? `(?=\\s+${after})` : ''}`, 'i'));
        } catch {
          /* an engine without look-behind - the next tier still applies */
        }
      }
      if (tier.length) tiers.push(tier);
    }
  }

  const opening = firstN(f.original_text || '', 8);
  if (opening.split(' ').length >= 3) tiers.push([new RegExp(loosePattern(opening), 'i')]);
  return tiers;
};
